import { useMemo, useState } from "react";
import { Check, ChevronsUpDown, Search } from "lucide-react";
import { fmt, fmtMoney, UNIT_LABEL } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";

const norm = (s) => (s || "").toString().toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");

export const FabricPicker = ({ fabrics, value, onChange, stockOf, priceField, placeholder = "Tapez un nom ou une référence…", testId = "fabric-picker", nextRef }) => {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const selected = fabrics.find((f) => f.id === value);
  const choose = (id) => { onChange(id); setOpen(false); setQ(""); setTimeout(() => { const el = nextRef?.current || document.querySelector(`[data-next-of="${testId}"]`); el?.focus(); el?.select?.(); }, 50); };
  const list = useMemo(() => {
    const nq = norm(q).trim();
    if (!nq) return fabrics.slice(0, 200);
    return fabrics.filter((f) => norm(`${f.reference} ${f.name} ${f.type}`).includes(nq)).slice(0, 200);
  }, [fabrics, q]);
  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (!o) setQ(""); }}>
      <PopoverTrigger asChild>
        <Button variant="outline" role="combobox" aria-expanded={open} className="w-full justify-between font-normal" data-testid={testId}>
          {selected ? <span className="truncate"><span className="font-mono text-xs text-muted-foreground">{selected.reference}</span> — {selected.name}</span> : <span className="flex items-center gap-2 text-muted-foreground"><Search size={14} /> {placeholder}</span>}
          <ChevronsUpDown size={14} className="ml-2 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] min-w-[320px] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput placeholder="Nom, référence, type…" value={q} onValueChange={setQ} autoFocus onKeyDown={(e) => { if (e.key === "Tab" && list.length) { e.preventDefault(); choose(list[0].id); } }} data-testid={`${testId}-input`} />
          <CommandList className="max-h-72">
            <CommandEmpty>Aucun tissu trouvé</CommandEmpty>
            <CommandGroup>
              {list.map((f) => {
                const unit = f.unit === "rouleau" ? "pc" : f.unit || "m";
                return (
                  <CommandItem key={f.id} value={f.id} onSelect={() => choose(f.id)} className="flex items-center gap-2" data-testid={`${testId}-item-${f.reference}`}>
                    <Check size={14} className={f.id === value ? "opacity-100" : "opacity-0"} />
                    <span className="w-24 shrink-0 font-mono text-xs text-muted-foreground">{f.reference}</span>
                    <span className="flex-1 truncate">{f.name}{f.type ? <span className="text-xs text-muted-foreground"> · {f.type}</span> : ""}</span>
                    <span className="shrink-0 text-xs text-muted-foreground">{priceField ? `${fmtMoney(f[priceField] || f.sale_price)}/${unit}` : UNIT_LABEL[f.unit] || unit}{stockOf ? ` · dispo ${fmt(stockOf(f.id))} ${unit}` : ""}</span>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};
